import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

const variants = {
  primary:
    'bg-secondary text-on-secondary hover:opacity-90 shadow-sm font-semibold',
  secondary:
    'bg-primary-container text-on-primary hover:opacity-90 font-semibold',
  ghost: 'bg-transparent text-primary hover:bg-surface-container font-semibold',
  danger: 'bg-error text-on-error hover:opacity-90 font-semibold',
} as const;

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof variants;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  loading?: boolean;
};

export function Button({
  className,
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled,
  loading,
  children,
  ...props
}: ButtonProps) {
  const sizes = {
    sm: 'px-4 py-2 text-sm min-h-[36px]',
    md: 'px-5 py-2.5 text-sm min-h-[44px]',
    lg: 'px-8 py-4 text-base min-h-[52px]',
  };

  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        sizes[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : null}
      {children}
    </button>
  );
}
